<script setup lang="ts">
export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

/** A native select. Native because it is keyboard-correct, mobile-correct and free. */
withDefaults(
  defineProps<{
    id?: string
    modelValue: string
    options: readonly SelectOption[]
    describedBy?: string
    disabled?: boolean
    /** Shown as a disabled first entry when the value is empty. */
    placeholder?: string
  }>(),
  { disabled: false },
)

defineEmits<{ 'update:modelValue': [string] }>()
</script>

<template>
  <div class="wrap">
    <select
      :id="id"
      class="o-select"
      :value="modelValue"
      :disabled="disabled"
      :aria-describedby="describedBy"
      @change="$emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
    >
      <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
      <option
        v-for="option in options"
        :key="option.value"
        :value="option.value"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </option>
    </select>
    <svg class="chevron" viewBox="0 0 12 12" aria-hidden="true">
      <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.5" />
    </svg>
  </div>
</template>

<style scoped>
.wrap {
  position: relative;
  display: block;
  min-width: 0;
}

.o-select {
  width: 100%;
  appearance: none;
  background: var(--bg-surface);
  color: var(--text-strong);
  border: var(--border-width) solid var(--border-strong);
  border-radius: var(--radius-md);
  padding: 0 var(--space-8) 0 var(--space-3);
  height: 2.125rem;
  font-size: var(--text-sm);
  cursor: pointer;
}

.o-select:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-ring);
}

.o-select:disabled {
  background: var(--bg-inset);
  color: var(--text-muted);
  cursor: not-allowed;
}

.chevron {
  position: absolute;
  right: var(--space-3);
  top: 50%;
  transform: translateY(-50%);
  width: 0.75rem;
  height: 0.75rem;
  color: var(--text-muted);
  pointer-events: none;
}
</style>
